import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CONTAINER_CLASS } from "../constants/layout";
import { Phone, ArrowRight, ChevronRight, ShoppingCart, MapPin, Package, Truck } from "lucide-react";
import Marquee from "react-fast-marquee";
import { Button } from "../components/ui/button";
import { servicePages, allBrands } from "../data/content";
import { AgenciesSection } from "../components/AgenciesSection";
import { useProducts } from "../hooks/useCatalog";
import ProductCard from "../components/catalog/ProductCard";
import { useStore } from "../context/StoreContext";

function ServiceProductsBlock({ slug, color, title }) {
  const { selectedStore } = useStore();
  const { items, loading } = useProducts({ limit: 8, sort: "newest" });
  if (loading && items.length === 0) return null;
  if (items.length === 0) return null;

  return (
    <section className="py-14 md:py-20 bg-white" data-testid={`service-products-${slug}`}>
      <div className={CONTAINER_CLASS}>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wider mb-1" style={{ color }}>
              Acheter en ligne
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">
              Nos produits {title?.toLowerCase() || ""}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Disponibles en ligne · Retrait en agence{selectedStore ? ` · ${selectedStore.city}` : ""}
            </p>
          </div>
          <Link
            to="/catalogue"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm transition-colors shadow-md hover:shadow-lg"
            style={{ backgroundColor: color }}
          >
            Voir tous les produits <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {items.map((p) => (
            <ProductCard key={p.id} product={p} showStock />
          ))}
        </div>
        <div className="text-center mt-8">
          <Link
            to="/catalogue"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#190276] text-white font-semibold hover:bg-[#130160] transition-colors shadow-md hover:shadow-lg"
          >
            Voir tout le catalogue <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function ServicePage() {
  const location = useLocation();
  const slug = location.pathname.replace("/", "");
  const data = servicePages[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!data) return <div className="pt-32 text-center">Page non trouvée</div>;

  const pageBrands = data.brands.length > 0
    ? allBrands.filter((b) => data.brands.includes(b.name))
    : allBrands;

  return (
    <div className="pt-0">
      {/* Hero Banner — même largeur max que le reste du site */}
      <section className="bg-white" data-testid={`service-hero-${data.slug}`}>
        <div className={CONTAINER_CLASS}>
          <div className="py-8 md:py-12">
            <nav aria-label="Fil d'Ariane" className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4">
              <Link to="/" className="hover:text-[#190276] transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="font-semibold text-gray-900">{data.slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}</span>
            </nav>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 text-center max-w-4xl mx-auto leading-tight"
            >
              {data.title}
            </motion.h1>
          </div>
          <div className="py-3 text-white font-bold text-sm" style={{ backgroundColor: data.color }}>
            <Marquee speed={40} gradient={false}>
              {Array(6).fill(null).map((_, i) => (
                <span key={i} className="mx-8">{data.marquee}</span>
              ))}
            </Marquee>
          </div>
        </div>
      </section>

      {/* Phone CTA — même largeur max */}
      <div className={CONTAINER_CLASS}>
        <div className="bg-[#f0f2f5] py-3 text-center">
          <a href="tel:0493903550" className="inline-flex items-center gap-2 text-lg font-bold" style={{ color: data.color }} data-testid="service-phone">
            <Phone className="w-5 h-5" /> 04 93 90 35 50
          </a>
        </div>
      </div>

      {/* Main content - Savoir-faire & expertise (WOW split) — même largeur max */}
      <section className={CONTAINER_CLASS}>
        <div className="flex flex-col lg:flex-row overflow-hidden">
          {/* Partie image - pleine hauteur */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative lg:w-[48%] min-h-[320px] lg:min-h-0"
          >
            <img src={data.heroImage} alt={data.intro.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-black/40 to-transparent" aria-hidden />
          </motion.div>
          {/* Partie contenu - bloc couleur */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center lg:w-[52%] py-14 md:py-20 px-6 md:px-12 lg:px-16 xl:px-20"
            style={{ backgroundColor: data.color }}
          >
            <div>
              <p className="text-[#F2B531] text-xs font-bold uppercase tracking-[0.2em] mb-4">Savoir-faire & expertise</p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">{data.intro.title}</h2>
              {data.intro.text.map((p, i) => (
                <p key={i} className="text-white/90 mb-4 text-base lg:text-lg leading-relaxed max-w-xl">{p}</p>
              ))}
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/catalogue" data-testid="service-catalogue-cta">
                  <span className="inline-flex items-center gap-2 bg-[#F2B531] text-gray-900 font-bold px-6 py-3 rounded-lg hover:bg-[#e5a82a] transition-colors shadow-lg">
                    Voir les produits <ShoppingCart className="w-5 h-5" />
                  </span>
                </Link>
                <Link to="/contact" data-testid="service-contact-cta">
                  <span className="inline-flex items-center gap-2 bg-white/20 text-white font-bold px-6 py-3 rounded-lg hover:bg-white/30 transition-colors border border-white/40">
                    Devis gratuit <ArrowRight className="w-5 h-5" />
                  </span>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Nos domaines d'expertise */}
      <section className="py-14 md:py-20 bg-[#f5f7fa]">
        <div className={CONTAINER_CLASS}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-14"
          >
            <p className="text-sm font-semibold uppercase tracking-wider mb-2" style={{ color: data.color }}>Nos domaines d'expertise</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Tout ce dont vous avez besoin</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {data.sections.map((s, i) => {
              const SectionIcon = s.icon ?? data.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="rounded-xl bg-white p-6 shadow-sm hover:shadow-md transition-shadow border-l-4"
                  style={{ borderLeftColor: data.color }}
                  data-testid={`service-section-${i}`}
                >
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: `${data.color}20` }}>
                    <SectionIcon className="w-5 h-5" style={{ color: data.color }} />
                  </div>
                  <h3 className="font-heading font-bold text-gray-900 text-lg mb-3">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Produits réels de cette catégorie */}
      <ServiceProductsBlock slug={slug} color={data.color} title={data.title} />

      {/* Bannière e-commerce */}
      <section className="py-6" style={{ backgroundColor: data.color }}>
        <div className={`${CONTAINER_CLASS} flex flex-wrap items-center justify-center gap-6 md:gap-12 text-white`}>
          {[
            { icon: ShoppingCart, text: "Commande en ligne" },
            { icon: MapPin, text: "Retrait en agence" },
            { icon: Package, text: "Stock réel" },
            { icon: Truck, text: "Livraison possible" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm font-semibold">
              <item.icon className="w-5 h-5 text-[#F2B531]" />
              {item.text}
            </div>
          ))}
        </div>
      </section>

      {/* Brands */}
      <section className="bg-white py-12 md:py-16">
        <div className={`${CONTAINER_CLASS} text-center`}>
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">Nos marques partenaires</h2>
          <p className="text-gray-500 mb-8">Votre garantie de qualité</p>
          <div className="flex flex-wrap justify-center gap-8 items-center">
            {pageBrands.map((b, i) => (
              <a key={i} href={b.url} target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity" data-testid={`brand-${b.name.toLowerCase()}`}>
                <img src={b.logo} alt={b.name} className="h-12 w-auto object-contain" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Next Service CTA */}
      <section className="py-12" style={{ backgroundColor: data.color }}>
        <div className={`${CONTAINER_CLASS} text-center`}>
          <p className="text-white/70 text-sm mb-2">Euro Plomberie Piscines — 5 agences de retrait + showroom Le Cannet</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">{data.nextService.title}</h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">{data.nextService.desc}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/catalogue">
              <Button className="btn-yellow" data-testid="catalogue-cta">
                Voir les produits disponibles <ShoppingCart className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to={data.nextService.link}>
              <Button variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="next-service-cta">
                {data.nextService.label} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <AgenciesSection />
    </div>
  );
}
